import { Form } from '@epages/react-components'
import { useState } from 'react'
import cc from 'classcat'

import { ImageEditor } from '../../../../../components/ImageEditor'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import CheckboxField from '../../../../formComponents/CheckboxField'
import FormGroup from '../../../../formComponents/FormGroup'
import FormValue from '../../../../formComponents/FormValue'
import InputField from '../../../../formComponents/InputField'
import LinkTooltipSettingsLayer from '../components/LinkTooltip/LinkTooltipSettingsLayer'

type Props = {
  data: ImmutableMap
  selectedImagePosition: number
  onApply: (image: ImmutableMap) => void
  onCancel: () => void
  imageUrl: (src: string) => string
  previewUrl: (src: string) => string
  storeFile: (file: File, config?: any) => Promise<Storage.Image>
  helpCenterUrl: string
}

export default function LogoEditPopover({
  data,
  selectedImagePosition,
  onApply,
  onCancel,
  imageUrl,
  previewUrl,
  storeFile,
}: Readonly<Props>) {
  const t = useTranslate('interface', 'components.logoElementComponent.logoEditModal')

  const [formData, setFormData] = useState<ImmutableMap>(
    data.get('images').find((img: ImmutableMap) => img.get('position') === selectedImagePosition),
  )

  const [showImageEditor, setShowImageEditor] = useState(false)

  const imageSrc = previewUrl(imageUrl(formData.get('src')))
  const isLinkEmpty = !formData.get('link')

  const handleChange = (value: ImmutableMap) => {
    setFormData(value)
  }

  const handleApplyButtonClick = () => onApply(formData)

  const handleCancelButtonClick = () => onCancel()

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      onApply(formData)
    }
  }

  const handleImageEditorInit: React.ComponentProps<typeof ImageEditor>['onInit'] = async (edit, imageFile) => {
    if (!formData.get('refSrc')) {
      const response = await storeFile(imageFile)
      setFormData((formData) =>
        formData.merge({
          edit,
          src: response.absoluteDownloadUrl,
          width: response.width,
          height: response.height,
          refSrc: formData.get('src'),
        }),
      )
    }
  }

  const handleImageEditorChange: React.ComponentProps<typeof ImageEditor>['onChange'] = async (
    edit,
    imageFile,
    newImageSourceFile,
  ) => {
    const imageUploads = [storeFile(imageFile)]
    if (newImageSourceFile && newImageSourceFile !== imageFile) {
      imageUploads.push(storeFile(newImageSourceFile))
    }

    const imageUploadResponses = await Promise.all(imageUploads)
    const imageUploadResponse = imageUploadResponses[0]
    const newSourceImageUploadResponse =
      imageFile === newImageSourceFile ? imageUploadResponses[0] : imageUploadResponses[1]

    setFormData((formData) =>
      formData.merge({
        edit,
        src: imageUploadResponse.absoluteDownloadUrl,
        width: imageUploadResponse.width,
        height: imageUploadResponse.height,
        refSrc: newSourceImageUploadResponse
          ? newSourceImageUploadResponse.absoluteDownloadUrl
          : formData.get('refSrc'),
      }),
    )

    setShowImageEditor(false)
  }

  const handleImageEditorCancel: React.ComponentProps<typeof ImageEditor>['onCancel'] = () => {
    setShowImageEditor(false)
  }

  const renderImage = () => (
    <>
      {!showImageEditor && (
        <div className="image-preview ep-form-row">
          <img src={imageSrc} alt="" />
          <div className="image-button-wrapper">
            <button
              type="button"
              className="dali-grid-element-edit-button dali-grid-element-edit-button-crop"
              title={t('editLogoButton.tooltip')}
              onClick={() => setShowImageEditor(true)}
            />
          </div>
        </div>
      )}
      {showImageEditor && (
        <ImageEditor
          src={imageUrl(formData.get('refSrc') || formData.get('src'))}
          aspectRatioMap={logoAspectRatioMap}
          initial={!formData.get('refSrc')}
          edit={
            formData.get('edit')?.toJS() || {
              aspectRatio: { label: 'original', value: formData.get('width') / formData.get('height') },
              offset: [0, 0],
              zoom: 1,
            }
          }
          onInit={handleImageEditorInit}
          onCancel={handleImageEditorCancel}
          onChange={handleImageEditorChange}
        />
      )}
    </>
  )
  return (
    <>
      {renderImage()}
      <Form name="logoEditPopover" value={formData} onChange={handleChange} onKeyDown={handleKeyDown}>
        <FormGroup
          info={t('alternativeTextInputField.explanation')}
          name="alt"
          tagName="label"
          label={t('alternativeTextInputField.label')}
        >
          <InputField name="alt" type="text" />
        </FormGroup>
        <FormValue name="link">
          {(link, setLink) => (
            <LinkTooltipSettingsLayer t={t} link={link} setLink={setLink} label={'linkInputField.label'} />
          )}
        </FormValue>
        <FormGroup
          className={cc(['ep-form-row-min-margin', { disabled: isLinkEmpty }])}
          name="opentab"
          tagName="label"
          label={t('openLinkInNewTabCheckbox.label')}
          labelAfterComponent
        >
          <CheckboxField name="opentab" disabled={isLinkEmpty} />
        </FormGroup>
        <ButtonRow>
          <Button type="button" variant="cancel" onClick={handleCancelButtonClick}>
            {t('cancelButton.label')}
          </Button>
          <Button type="button" onClick={handleApplyButtonClick}>
            {t('applyButton.label')}
          </Button>
        </ButtonRow>
      </Form>
    </>
  )
}

const logoAspectRatioMap = {
  '2:3': 2 / 3,
  '1:1': 1,
  '3:2': 3 / 2,
  '3:1': 3 / 1,
  '4:1': 4 / 1,
}
